import { appName } from '../config'

/**
 * Constants
 * */
export const moduleName = `content`
const prefix = `${appName}/${moduleName}`

export const CHANGE_ITEM = `${prefix}/CHANGE_ITEM`

export const CHANGE_ROOT_ITEM_START = `${prefix}/CHANGE_ROOT_ITEM_START`
export const CHANGE_ROOT_ITEM_SUCCESS = `${prefix}/CHANGE_ROOT_ITEM_SUCCESS`
export const CHANGE_ROOT_ITEM_FAIL = `${prefix}/CHANGE_ROOT_ITEM_FAIL`

export const CHANGE_OPTION_START = `${prefix}/CHANGE_OPTION_START`
export const CHANGE_OPTION_SUCCESS = `${prefix}/CHANGE_OPTION_SUCCESS`
export const CHANGE_OPTION_FAIL = `${prefix}/CHANGE_OPTION_FAIL`

export const SHOW_PRICE_MODAL = `${prefix}/SHOW_PRICE_MODAL`
export const HIDE_PRICE_MODAL = `${prefix}/HIDE_PRICE_MODAL`

/**
 * Reducer
 * */

export default function reducer(state = {}, action) {
  const { type, payload, error } = action

  switch (type) {
    case CHANGE_ITEM:
      return {
        ...state,
        activeItem: payload.name,
      }

    case CHANGE_ROOT_ITEM_START:
      return {
        ...state,
        loading: true,
      }

    case CHANGE_ROOT_ITEM_SUCCESS:
      return {
        ...payload.item,
        loading: false,
      }

    case CHANGE_ROOT_ITEM_FAIL:
      return {
        ...state,
        error,
        loading: false,
      }

    case CHANGE_OPTION_START:
      return state.items
        ? {
            ...state,
            items: state.items.map((item) => {
              if (item.singular === state.activeItem) {
                return {
                  ...item,
                  condition: {
                    ...item.condition,
                    options: {
                      ...item.condition.options,
                      [payload.name]: payload.value,
                    },
                  },
                  loading: true,
                }
              }
              return item
            }),
          }
        : {
            ...state,
            condition: {
              ...state.condition,
              options: {
                ...state.condition.options,
                [payload.name]: payload.value,
              },
            },
            loading: true,
          }

    case CHANGE_OPTION_SUCCESS:
      return state.items
        ? {
            ...state,
            items: state.items.map((item) => {
              if (item.singular === state.activeItem) {
                return {
                  ...item,
                  editorPath: transformEditorPath(
                    item.editorPath,
                    item.condition.options
                  ),
                  view: payload.view,
                  condition: {
                    ...item.condition,
                    price: payload.price,
                  },
                  loading: false,
                }
              }
              return item
            }),
          }
        : {
            ...state,
            editorPath: transformEditorPath(
              state.editorPath,
              state.condition.options
            ),
            view: payload.view,
            condition: {
              ...state.condition,
              price: payload.price,
            },
            loading: false,
          }

    case CHANGE_OPTION_FAIL:
      return {
        ...state,
        error,
        loading: false,
      }

    case SHOW_PRICE_MODAL:
      return { ...state, isPriceModalOpen: true }

    case HIDE_PRICE_MODAL:
      return { ...state, isPriceModalOpen: false }

    default:
      return state
  }
}

const convertOptionsToString = (options) => {
  let string = ``
  for (let prop in options) {
    if ({}.hasOwnProperty.call(options, prop)) {
      string += `&variant[${prop}]=${options[prop]}`
    }
  }
  return string
}

const convertOptionsToStringWithVerticalLine = (options) =>
  Object.keys(options)
    .map((key) => `${key}:${options[key]}`)
    .join(`|`)

function transformEditorPath(editorPath, options) {
  const base = editorPath.split(`?`)[0]
  const params = `?variant=${convertOptionsToStringWithVerticalLine(options)}`
  return `${base}${params}`
}

/**
 * Selectors
 * */

export const contentSelector = (state) => state[moduleName]
export const typeOfContentSelector = (state) => state[moduleName].type
export const nameOfContentSelector = (state) => state[moduleName].name
export const seoOfContentSelector = (state) => state[moduleName].seo
export const titleOfContentSelector = (state) => state[moduleName].title
export const singularOfContentSelector = (state) => state[moduleName].singular
export const departmentOfContentSelector = (state) =>
  state[moduleName].department
export const itemsOfContentSelector = (state) => state[moduleName].items
export const activeItemNameOfContentSelector = (state) =>
  state[moduleName].activeItem
export const activeItemOfContentSelector = (state) => {
  if (nameOfContentSelector(state) === `root`) {
    return contentSelector(state)
  }
  const activeItemName = activeItemNameOfContentSelector(state)
  const items = itemsOfContentSelector(state)

  return (
    items.filter(({ singular }) => activeItemName === singular)[0] || items[0]
  )
}
export const activeItemPopularProducts = (state) => {
  return activeItemOfContentSelector(state).popularProducts
}
export const activeItemOtherProducts = (state) => {
  return activeItemOfContentSelector(state).otherProducts
}

/**
 * Action Creators
 * */

export const changeItem = (name) => (dispatch) => {
  dispatch({
    type: CHANGE_ITEM,
    payload: {
      name,
    },
  })
}

export const changeRootItem = (name) => (dispatch) => {
  dispatch({
    type: CHANGE_ROOT_ITEM_START,
  })
  fetch(`/api/products/${name}?format=json`, {
    headers: {
      Accept: `application/json`,
    },
    credentials: `same-origin`,
  })
    .then((res) => res.json())
    .then((item) => {
      dispatch({
        type: CHANGE_ROOT_ITEM_SUCCESS,
        payload: {
          item,
        },
      })
    })
    .catch((error) => {
      dispatch({
        type: CHANGE_ROOT_ITEM_FAIL,
        error,
      })
    })
}

const cacheOfViews = {}
const cacheOfPrice = {}
const fetchViews = (name, options) => {
  const stringOptions = convertOptionsToString(options)
  if (cacheOfViews[stringOptions]) {
    return cacheOfViews[stringOptions]
  }
  return fetch(`/background_urls/${name}.json?style=teaser${stringOptions}`, {
    headers: {
      Accept: `application/json`,
    },
    credentials: `same-origin`,
  }).then((res) => {
    return res.json()
  })
}

const getViewsByOptions = async (name, options) => {
  try {
    return await fetchViews(name, options)
  } catch (err) {
    throw err
  }
}

const getPriceByOptions = async (name, options) => {
  try {
    const stringOptions = convertOptionsToStringWithVerticalLine(options)
    const cacheKey = `${name}-${stringOptions}`

    if (cacheOfPrice[cacheKey]) {
      return cacheOfPrice[cacheKey]
    }
    return await fetch(
      `/blank_item_prices/${name}.json?variant=${stringOptions}&only_amount=true`,
      {
        headers: {
          Accept: `application/json`,
        },
        credentials: `same-origin`,
      }
    ).then((res) => {
      const result = res.json()
      cacheOfPrice[cacheKey] = result
      return result
    })
  } catch (err) {
    throw err
  }
}

export const changeOption = (name, value) => async (dispatch, getState) => {
  if (value) {
    try {
      dispatch({
        type: CHANGE_OPTION_START,
        payload: {
          name,
          value,
        },
      })
      const {
        singular,
        condition: { options },
      } = activeItemOfContentSelector(getState())
      const { blank, background } = await getViewsByOptions(singular, {
        ...options,
        [name]: value,
      })
      const {
        base_price: basePrice,
        current_price: currentPrice,
      } = await getPriceByOptions(singular, {
        ...options,
        [name]: value,
      })

      dispatch({
        type: CHANGE_OPTION_SUCCESS,
        payload: {
          name,
          value,
          view: blank || background,
          price: {
            basePrice,
            currentPrice,
          },
        },
      })
    } catch (error) {
      dispatch({
        type: CHANGE_OPTION_FAIL,
        error,
      })
    }
  }
}

export const showPriceModal = () => ({
  type: SHOW_PRICE_MODAL,
})

export const hidePriceModal = () => ({
  type: HIDE_PRICE_MODAL,
})
